import { defineAsyncComponent, ref, reactive, getCurrentInstance, ComponentInternalInstance, onUnmounted } from "vue";
import { CalculatedCallback } from './CalcHelperStatic';
import { getKennungTitle, getKennungValueDisplay, getValue, getKennungValue } from "@/DataHelper";
import { Globals } from "@/Globals";
import { AxiosResponse } from "axios";
import { showDialog, showSnackbar } from "@/UIHelper";
import { getSiteData, refreshFullPageContent } from "@/NavigationHelper";
import AUApi from "@/api";
import { calcFunctionNew } from "@/CalcHelperStatic";
import { globalsBezeichnung, globalsDruck, globalsFrame, globalsStt, globalsBegriffMobile, globalsKennung, globalsBegriffMemo } from "@/local";


declare interface EditPageCacheEntry {
  uiData: DataPageUIData;
  pageParams: DataPageParameter;
  instance: ComponentInternalInstance
}
declare interface EditPageCache {
  [id: number | string]: EditPageCacheEntry;
}

const editPageCache: EditPageCache = {};

export function getEditPageByInstanceId(id: number) {
  return editPageCache[id];
}

// Fehler aus einem (Node-)Request als Snackbar ausgeben; deckt Body-Formen wie
// { message, statusCode } sowie { error } / { info } ab.
function showRequestError(ex: any) {
  const data = ex?.response?.data;
  const status = ex?.response?.status ?? ex?.request?.status ?? data?.statusCode;
  const text =
    data?.message ??
    data?.info ??
    data?.error ??
    ex?.message ??
    (typeof ex === "string" ? ex : "Unbekannter Fehler");
  showSnackbar({
    text,
    color: "error",
    duration: status === 401 ? 6000 : 4000,
  });
}

export function BaseSetup<
  PageParams extends DataPageParameter = DataPageParameter
  , PageUiData extends DataPageUIData = DataPageUIData>(pageParams: PageParams, defaultUiData?: Partial<PageUiData>)
  : PageUiData {
  const instance = getCurrentInstance()
  const uidata = <PageUiData><DataPageUIData>{
    vueInstance: instance,
    zeitbereich: pageParams.zeitbereich ?? AUApi.Current.zeitbereich,
    header: { bezeichnung: "??" } as QueryHeader,
    data: {} as QueryData,
    columns: [],
    insertHint: false,
    printItems: [],
    modelle: [],
    exportItems: [],
    initialized: false,
    showLoading: true,
    avatarIcon: "",
    tabItems: [],
    showCalc: false,
    nodeRequest: [],
    saveNodeRequest: "",
  };

  if (instance) {
    editPageCache[instance.uid] = { uiData: uidata, pageParams, instance };
    //console.log("Setup add pageCache", instance.uid, Object.keys(editPageCache).length);

    onUnmounted(() => {
      delete editPageCache[instance.uid];
      //console.log("onUnmounted remove pageCache", instance.uid, Object.keys(editPageCache).length);
    }, instance);

  } else
    console.warn("No instance found for cashing!");

  if (defaultUiData) {
    Object.assign(uidata, defaultUiData);
  }
  if (pageParams.query === "" && !pageParams.preloadedData) {
    // ist die Abfrage leer - dann brauch ich nichts laden!
    return uidata;
  }
  if (pageParams.aic) {
    pageParams.queryAIC = pageParams.aic;
  }
  uidata.insertHint = pageParams.insertMode ?? false;
  const siteData = getSiteData();
  if (siteData) {
    if (siteData.query) {
      pageParams.query = siteData.query;
    }
    if (siteData.preloadedData) {
      pageParams.preloadedData = siteData.preloadedData;
    }
    if (siteData.aic && typeof siteData.aic !== "object") {
      pageParams.queryAIC = siteData.aic;
    }
    if (siteData.zeitbereich) {
      pageParams.zeitbereich = siteData.zeitbereich;

    }
    if (siteData.tag) {
      pageParams.zeitbereich = {
        von: siteData.tag,
        bis: siteData.tag,
        bereich: "Tag"
      };
    }
    if (siteData.insertHint) {
      uidata.insertHint = siteData.insertHint ?? false;
    }
    if (siteData.bewegungsdaten) {
      pageParams.bewegungsdaten = siteData.bewegungsdaten;
    }
    if (siteData.site) {
      const siteName = siteData.site.compNameFrm ?? siteData.site.kennung;
      const frame = globalsFrame(Globals.Begriff, false, "", "", false, siteName);
      if (frame[0].mod2) {
        uidata.modelle = frame[0].mod2;
      }
      uidata.exportItems = frame[0].abf2;
    }
    if (siteData?.mainAic) {
      pageParams.mainAic = siteData.mainAic; // Aufrufenden AIC zum Speichern übergeben!
    }
    if (siteData.newData) {
      pageParams.newData = siteData.newData;
    }
  }
  return uidata;
}

export async function mountedBase(data: DataPageUIData, param: DataPageParameter, setInitializedOnEnd: boolean = true) {
  data.zeitbereich = param.zeitbereich ?? AUApi.Current.zeitbereich;
  if (param.preloadedData) {
    await dataLoaded(data, param, param.preloadedData!);
    //  initialize();
  } else {
    await reloadData(data, param, setInitializedOnEnd);
  }
}

export async function reloadData(uidata: DataPageUIData, param: DataPageParameter, setInitializedOnEnd: boolean = true) {
  if (param.query != null && param.query != "") {
    uidata.showLoading = true;
    let vecStamm: any[] = [];
    // das passt noch nicht... VEC Stamm sollte doch  Übergeben werden
    if (param.useVec) {
      vecStamm = vecStamm;
    }
    let varUebergabe: VarUebergabe[] = [];
    const siteData = getSiteData();
    if (siteData && siteData?.tag) {
      param.zeitbereich = {
        von: siteData.tag,
        bis: siteData.tag,
        bereich: "Tag",
      };
    }
    if (siteData && siteData?.varUbergabe) {
      varUebergabe = siteData.varUbergabe;
    }
    const begriffAbfrage = globalsKennung(
      Globals.Begriff,
      param.query
    );
    let nodeRequest: string = "";
    let nodeNachSpeichern: boolean = false;
    if (begriffAbfrage && begriffAbfrage.zuordnung) {
      uidata.nodeRequest = begriffAbfrage.zuordnung?.filter((z: any) => z.gruppe === "Node");
      if (uidata.nodeRequest && uidata.nodeRequest.length > 0) {
        // art 3 == nach Speichern
        const saveRequest = uidata.nodeRequest?.filter((z: any) => z.art.nr === 3);
        if (saveRequest && saveRequest.length > 0) {
          uidata.saveNodeRequest = saveRequest[0].nodeJS;
          nodeNachSpeichern = true;
        }
        // art 2 = Aufruf
        const nodeRequestDaten = uidata.nodeRequest?.filter((z: any) => z.art.nr === 2);
        if (nodeRequestDaten && nodeRequestDaten.length > 0) {
          nodeRequest = nodeRequestDaten[0].nodeJS;
        }
      }
    }
    if (nodeRequest) {
      const response = await AUApi.Current.sendNodeRequest(
        nodeRequest,
        param.query,
        newdata => dataLoaded(uidata, param, newdata),
        param.queryAIC,
        param.zeitbereich,
        undefined, // header
        undefined, // DataToSave
        uidata.formular?.aic,
        undefined, //columns
        vecStamm,
        param.newData,
        param.bewegungsdaten,
        [], // TabÜbergabe - ModellDialog
        param.jokerStt,
        "", // kein Austritt
        varUebergabe,
      );
      if (!response) {
        uidata.showLoading = false;
        uidata.initialized = setInitializedOnEnd;
      }
    } else {
      const response = await AUApi.Current.getQuery(
        // Query
        param.query,
        // Done function
        newdata => dataLoaded(uidata, param, newdata),
        param.queryAIC,
        param.zeitbereich,
        0,
        false,
        param.bewegungsdaten,
        "",
        uidata.formular?.aic,
        vecStamm,
        "",
        param.newData,
        0,
        [],
        param.jokerStt,
        "",
        varUebergabe,
        0,
      );
      if (!response) {
        uidata.showLoading = false;
        uidata.initialized = setInitializedOnEnd;
      }
    }
  } else {
    uidata.showLoading = false;
    uidata.initialized = setInitializedOnEnd;
  }
}

export async function dataLoaded(uidata: DataPageUIData, param: DataPageParameter, response: AxiosResponse<QueryResponse>) {
  try {
    if (response.data.error !== undefined) {
      AUApi.Current.onfail(response.data.error);
      console.log(response.data.error);
      alert("ACHTUNG: " + response.data.error);
      param.emitBack();
      return;
    }

    // diese Schleife initialisiert die filter Property auf da Spalte die es vom Server her nicht gibt ;)
    for (let i = 0; i < response.data.columns.length; i++) {
      response.data.columns[i].filter = "";
    }
    const queryResp = response.data as QueryResponse;

    uidata.header = queryResp.header;
    uidata.headerStamm = queryResp.header.aicStt;
    uidata.columns = queryResp.columns;
    uidata.data = queryResp.data;
    // bei NewData muss ich ein leeres Array künstlich machen - sonst klappt das speichern nicht!
    if (param.newData && uidata.data.length === 0) {
      uidata.data.push({});
    }
    const tabs: any = uidata.columns.filter(
      (c: any) => c.meta?.step !== "" && c.meta.step !== undefined && c.meta.subAbf !== undefined
    );
    if (tabs.length > 0 && uidata.initialized) {
      uidata.tabItems = [];
      tabs.forEach((r: any) => {
        let active = false;
        let value = 0;
        let valueDisplay = "";
        // wenn "Zahl" - dann gibt es eine Info ob es Daten zu dem Folder/Tab gibt!
        if (r.type === "zahl" && uidata.data) {
          value = getValue(uidata.data[0], r);
          if (typeof value === "number") {
            if (r.meta?.format === "0.00") {
              valueDisplay = value.toFixed(2);
            } else {
              valueDisplay = value.toFixed(0);
            }
          } else {
            valueDisplay = value;
          }
          if (value) {
            active = true;
          }
        }
        let query = "";
        const step = r.meta?.step;
        let component = r.meta.webDarstellung ?? "BaseVGrid";

        if (r.meta?.subFom && r.meta?.subForm.lenght > 0) {
          const subForm = r.meta?.subFom[0];
          component = subForm.alias ?? subForm.kennung;
        } else {
          query = r.meta?.subAbf[0]?.kennung;
        }
        if (!uidata.tabItems) uidata.tabItems = [];
        uidata.tabItems.push({
          text: r.title,
          icon: r.meta.icon ?? "info",
          color: r.meta.color ?? "success",
          step: parseInt(step, 10),
          component,
          active,
          query,
          activeNumber: valueDisplay,
        });
      });
    }
    addDruck(uidata, param)
    uidata.showLoading = false;
  }
  catch (err) {
    console.log(err);
  }
}
// abstract function refreshItems(): void;
// beim Löschen von Stammsätzen muss unbedingt gefragt werden ob die Daten gelöscht werden sollen!
export async function deleteStt(uidata: DataPageUIData, param: DataPageParameter, selected: number) {
  if (!uidata.header?.aicBew) {
    if (
      confirm(
        "Wollen Sie den Datensatz wirklich löschen?"
      )) {
      const res = await onDelete(uidata, param, selected, "");
      return res;
    }
  }
}
export async function onDeleteAsync(uidata: DataPageUIData, param: DataPageParameter, aic: number) {
  onDelete(uidata, param, aic, "");
}
export async function onDelete(uidata: DataPageUIData, param: DataPageParameter, aic: number, modell: string, zeitbereich?: any, ohnePruefung?: boolean, onCalculated: CalculatedCallback = null) {
  try {
    if (!aic) {
      showSnackbar("keinen Datensatz übergeben!");
      // gibt es keinen AIC Dann abbrechen! damit nix falsches gelöscht wird
      return;
    }
    const bewPool = aic;
    const res = await AUApi.Current.DeleteData(uidata.header, bewPool, 0, zeitbereich, ohnePruefung);

    if (res) {
      const msgType = res.data.msgType;
      let info = res.data.info;
      const memo = res.data.memo;
      const title = res.data.titel;
      if (memo) {
        info = memo;
      }
      if (info) {
        if (msgType === "Error") {
          showSnackbar({
            text: info,
            color: "error"
          });
        } else if (msgType === "Info") {
          showSnackbar({
            text: info,
            color: "info"
          });
        } else if (msgType === "Warnung") {
          showSnackbar({
            text: info,
            color: "warning"
          });
        }
        uidata.showLoading = false;
        return msgType;
      }
      showSnackbar(
        globalsBegriffMemo(
          Globals.Begriff,
          "Datensatz_geloescht",
          "Daten wurden gelöscht"
        )
      ); // Geloescht
    }
    if (modell) {
      await calcFunctionNew(modell, AUApi.Current.user.aic, false, onCalculated, param.zeitbereich, []).then(() => {
        backOrReload(uidata, param);
      });
    } else {
      backOrReload(uidata, param);
    }
  } catch (ex: any) {
    if (ex && ex.response) {
      showRequestError(ex);
    }
    console.log(ex);
    if (ex !== AUApi.Current.ApiBusyMessage) {
      showSnackbar(
        globalsBegriffMemo(
          Globals.Begriff,
          "Berechnung_lauft",
          "Berechnung läuft, bitte warten Sie diese ab."
        )
      );
    }
  };
}
function backOrReload(uidata: DataPageUIData, param: DataPageParameter) {
  if (param.backOnDelete) {
    // nix tun...
  } else if (param.backOnSave) {
    param.emitBack(true);
  } else {
    reloadData(uidata, param);
  }
}

function reviveZeitbereich(zeitbereich?: APIZeitbereich) {
  if (!zeitbereich) {
    return zeitbereich;
  }

  return {
    ...zeitbereich,
    von: zeitbereich.von ? new Date(zeitbereich.von) : zeitbereich.von,
    bis: zeitbereich.bis ? new Date(zeitbereich.bis) : zeitbereich.bis,
  };
}

export function createLocalSaveRequest(
  uidata: DataPageUIData,
  param: DataPageParameter,
  aicAbfAll?: number,
  zeitbereich?: APIZeitbereich | undefined,
  aic?: number,
  varUebergabe?: any[],
  nodeRequest?: string,
  stichtag?: Date | null
) {
  let AbfAll = 0;
  if (typeof aicAbfAll === "number") {
    AbfAll = aicAbfAll;
  }

  let aicUebergabe = aic;
  if (!aicUebergabe) {
    if (uidata.header?.aicBew) {
      aicUebergabe = aic;
    } else {
      aicUebergabe = aic ?? param.queryAIC;
    }
  }

  let saveHaupt = false;
  if (param.mainAic && uidata.header?.aicBew) {
    aicUebergabe = param.mainAic;
    saveHaupt = true;
  }

  return {
    header: uidata.header,
    aicAbfAll: AbfAll,
    aicBewPool: uidata.data?.[0]?.aic_Bew_pool,
    data: uidata.data?.[0],
    columns: uidata.columns,
    zeitbereich: zeitbereich ?? param.zeitbereich,
    backupData: param.backupData,
    aicUebergabe,
    saveHaupt,
    mainStt: param.mainStt,
    stichtag,
    jokerStt: param.jokerStt,
    varUebergabe,
    mehrfach: uidata.header?.tabellenTyp === "Mehrfach",
    nodeRequest,
  };
}

export async function executeLocalSaveRequest(request: any) {
  return await AUApi.Current.SaveData(
    request.header,
    request.aicAbfAll,
    request.aicBewPool,
    request.data,
    request.columns,
    reviveZeitbereich(request.zeitbereich),
    request.backupData,
    null,
    (ex: any) => {
      throw ex;
    },
    request.aicUebergabe,
    false,
    request.saveHaupt,
    request.mainStt,
    request.stichtag ? new Date(request.stichtag) : request.stichtag,
    request.jokerStt,
    request.varUebergabe,
    request.mehrfach,
    request.nodeRequest
  );
}
// speichern und dann gleich ein Modell auslösen!
export async function onSave(
  uidata: DataPageUIData, param: DataPageParameter,
  aicAbfAll?: number,
  modell?: string,
  zeitbereich?: APIZeitbereich | undefined,
  aic?: number,
  getData?: boolean,
  varUebergabe?: any[],
  nodeRequest?: string,
  stichtag?: Date | null,
  onCalculated: CalculatedCallback = null
) {
  try {
    // Modul-global - sonst traegt ein alter Wert aus einem frueheren Speichern
    // hier ein zweites param.emitBack() mit veralteten Daten los.
    updateDataForAsync = null;
    let AbfAll = 0;
    if (typeof aicAbfAll === "number") {
      AbfAll = aicAbfAll; // Abfrage für Planung speichern
    }
    const isvalid = onValidate(uidata, param);
    if (!isvalid) {
      showSnackbar({
        text: "Validierung fehlgeschlagen!",
        duration: 4000,
        color: "error"
      });
      return;
    }
    if (zeitbereich) {
      zeitbereich = zeitbereich;
    }
    const setDataTrue = getData;
    uidata.showLoading = true;
    let aicUebergabe = aic; // muss der immer gesetzt sein ?
    if (!aicUebergabe) {
      if (uidata.header?.aicBew) {
        aicUebergabe = aic;
      } else {
        aicUebergabe = aic ?? param.queryAIC;
      }
    }
    let saveHaupt = false;
    const siteData = getSiteData();
    if (param.mainAic && uidata.header?.aicBew) {
      getData = true;
      aicUebergabe = param.mainAic;
      saveHaupt = true;
    }
    let mehrfach = false;
    if (uidata.header.tabellenTyp === "Mehrfach") {
      // bei Mehrfach Tabellen, zB Firmenkonto - muss ich den führenden Stammsatz mitgeben, damit darauf gespeichert werden kann
      mehrfach = true;
    }
    const result = await AUApi.Current
      .SaveData(
        uidata.header,
        AbfAll,
        uidata.data[0].aic_Bew_pool,
        uidata.data[0],
        uidata.columns,
        param.zeitbereich,
        param.backupData,
        null, // nicht mehr hier
        (ex: any) => {
          throw ex
        },
        aicUebergabe,
        getData,
        saveHaupt,
        param.mainStt,
        stichtag,
        param.jokerStt,
        varUebergabe,
        mehrfach,
        nodeRequest,
      )

    saved(uidata, param, result, modell);
    // nach dem Speichern soll gleich ein Modell aufgerufen werden!
    if (modell) {
      // es dar nur der User AIC gesetzt werden, wenn es keinen abweichenden gibt!
      await calcFunctionNew(modell, AUApi.Current.user.aic, false, onCalculated, param.zeitbereich, []).then(() => {
        // danach ggf zumachen...
        // await calcFunctionNew(modell, aicUebergabe ?? AUApi.Current.user.aic, false, onCalculated, param.zeitbereich, []).then(() => {
        // danach ggf zumachen...
        if (param.backOnSave) {
          param.emitBack(updateDataForAsync);
        }
      });
    } else if (getData && updateDataForAsync) { // ergebnis an closeThisDialog zurück schicken??
      // @Stefan;
      // wahrschenlich nicht die eleganteste LÖsung aber ich bekomm es mal in die Stempleungem wieder rein...
      if (siteData) {
        siteData.returnedData = updateDataForAsync;
      }
      if (param.backOnSave) {
        param.emitBack(updateDataForAsync);
      }
    }
    if (result) {
      // was jetzt?
    }
    return result;
  } catch (ex: any) {
    uidata.showLoading = false;
    showRequestError(ex);
    console.error("ERROR: " + ex);
  }
}
function onValidate(uidata: DataPageUIData, param: DataPageParameter) {
  let valid: boolean = true;
  if (param.validations) {
    for (let i = 0; i < param.validations?.length; i++) {
      const val = param.validations[i];
      const res = val.isValid() as boolean;
      valid = valid && res;
      console.log("field " + val.field.column.name + " -> " + res);
    }
  }
  return valid;
}
let updateDataForAsync: any = null;

export function saved(uidata: DataPageUIData, param: DataPageParameter, res: any, modell: any) {
  if (res.data) {
    const msgType = res.data.msgType;
    let info = res.data.info;
    const memo = res.data.memo;
    const title = res.data.titel;
    if (memo) {
      info = memo;
    }
    if (info) {
      if (msgType === "Error") {
        showSnackbar({
          text: info,
          duration: 6000,
          color: "error"
        });
      } else if (msgType === "Info") {
        showSnackbar({
          text: info,
          duration: 6000,
          color: "info"
        });
      } else if (msgType === "Warnung") {
        showSnackbar({
          text: info,
          duration: 6000,
          color: "warning"
        });

      }
      uidata.showLoading = false;
      return msgType;
    }
    if (res.data!.JSON && res.data.JSON.fehler) {
      const fehler = res.data.JSON.fehler;

      showSnackbar({
        text: fehler,
        duration: 6000,
        color: "warning"
      });
    }
  }
  uidata.showLoading = false;
  if (!res.nothingHappend) {
    // wenn nix passiert is aber weitermachen ;)
    showSnackbar({
      text: globalsBegriffMemo(
        Globals.Begriff,
        "Datensatz_gespeichert",
        "Daten wurden gespeichert"
      ),
      duration: 4000,
    }
    );
  } else {
    showSnackbar(
      globalsBegriffMemo(
        Globals.Begriff,
        "Datensatz_keine_Aenderung",
        "Daten wurden nicht verändert, kein Speichern durchgeführt!"
      )
    );
  }
  let updateData = null;
  if (res && res.data) {
    updateData = res.data;
  }
  const siteData = getSiteData();
  if (siteData) {
    siteData.reloadData = true;
    if (updateData && updateData.data && updateData.data.length > 0) {
      siteData.aic = updateData.data[0].aic_Bew_pool ?? updateData.data[0].aic_Stamm;
      siteData.updateData = updateData.data[0];
    }
  }
  // backOnSave nur wenn nicht modellberechnung danach gefordert.
  if (param.backOnSave && !modell) {
    param.emitBack(updateData);
  } else {
    updateDataForAsync = updateData;
  }
  if (updateData && updateData.data && updateData.data.length > 0) {
    const update = updateData.data[0];
    uidata.data[0].aic_Bew_pool = update.aic_Bew_pool;
    uidata.initialized = false;
    uidata.columns.forEach(col => {
      if (update[col.name] !== undefined) {
        uidata.data[0][col.name] = update[col.name];
        console.log("updated " + col.name + " -> " + update[col.name]);
      }
    });
    uidata.initialized = true;
  } else if (updateData.aic_Bew_pool) {
    // wird nur der aic_Bew_pool zurück geliefert, dann auch diesen ersetzen!
    uidata.data[0].aic_Bew_pool = updateData.aic_Bew_pool;
  }
}

export async function onCancel(uidata: DataPageUIData, param: DataPageParameter) {
  param.emitBack();
}
function addDruck(uidata: DataPageUIData, param: DataPageParameter) {
  const siteData = getSiteData();
  if (!uidata.header.aicBew && uidata.printItems.length === 0) {
    const stamm = uidata.header.aicStt;
    uidata.printItems = globalsDruck(Globals.Begriff, false, stamm, true);
  } else if (siteData?.item?.compTitle) {
    const comp = siteData.item.compTitle;
    const frame = globalsFrame(Globals.Begriff, false, "", comp);
    uidata.printItems = frame[0].druck;
    // für das Formular drucke suchen...
  }
}
export function openDruckVorschau(uidata: DataPageUIData, param: DataPageParameter, item: any) {
  const printItem = item;
  let fullscreen = false;
  // if (Constants.isMobile && isPhone()) {
  //   fullscreen = true;
  // }
  let aic = param.queryAIC;
  if (!aic && param.aic) {
    aic = param.aic;
  }
  const noSelect = true;
  showDialog({
    title: printItem.bezeichnung,
    site: "BaseDruck",
    fullscreen,
    width: 510,
    data: {
      zeitbereich: param.zeitbereich,
      printItem,
      aic,
      pers: true,
      noSelect,
    },
    onClose: () => {
      return true;
      /* nix tun*/
    },
  });
}
export function loadImage(uidata: DataPageUIData, param: DataPageParameter, avatar: any) {
  if (!avatar.data) {
    uidata.avatarIcon = AUApi.Current.fullUrl(
      "imageM/" + AUApi.Current.user.id + ":" + +avatar.aic + "_" + avatar.name
    );
  }
}
export async function loadAmpel(
  uidata: DataPageUIData, param: DataPageParameter,
  query: string,
  bool3?: boolean,
  status?: boolean,
  aic?: number,
  checkData?: boolean
) {
  const setzBool3 = bool3;
  const statusSetzen = status;
  const ampelQuery = query;
  const vecStamm: any[] = [];
  let aicUebergabe = param.queryAIC;
  if (aic) {
    aicUebergabe = aic;
  }
  let formAic = 0;
  if (uidata.formular) {
    formAic = uidata.formular?.aic;
  }
  // Ampel benötigt immer einen Monat; ist nur ein Tag eingestellt, auf den Monat erweitern
  let ampelZeitbereich = param.zeitbereich;
  if (ampelZeitbereich && ampelZeitbereich.bereich !== "Monat") {
    const von = new Date(ampelZeitbereich.von);
    ampelZeitbereich = {
      bereich: "Monat",
      von: new Date(von.getFullYear(), von.getMonth(), 1),
      bis: new Date(von.getFullYear(), von.getMonth() + 1, 0),
    };
  }
  return await AUApi.Current.getQuery(
    ampelQuery,

    (data: any) => showAmpelData(uidata, param, data, setzBool3, statusSetzen, checkData),
    aicUebergabe,
    ampelZeitbereich,
    0,
    false,
    false,
    "",
    formAic,
    vecStamm
  );
}
function showAmpelData(
  uidata: DataPageUIData, param: DataPageParameter,
  ampelData: any,
  setzBool3?: boolean,
  status?: boolean,
  checkData?: boolean
) {
  if (ampelData.data.error !== undefined) {
    AUApi.Current.onfail(ampelData.data.error);
    console.log(ampelData.data.error);
    alert("ACHTUNG: " + ampelData.data.error);
    param.emitBack();
    return;
  }
  const isBool3 = setzBool3;
  const isStatus = status;
  const datenAmpel = ampelData.data.data;
  uidata.columns = ampelData.data.columns;
  if (datenAmpel.length > 0) {
    const row = datenAmpel[0];
    uidata.ampelZulage = getKennungValue(row, "ampel", uidata.columns);
    uidata.zulagenAmpel = uidata.ampelZulage;
    let color: string = "";
    if (checkData) {
      uidata.colorZulagen = "warning";
    } else if (isBool3) {
      if (uidata.ampelZulage === 1) {
        color = "success";
        uidata.showCalc = false;
      } else if (uidata.ampelZulage === 3) { // 3 gibts eigentlich nicht - NR bei 3 er Boolean ist - 0,1,2 
        color = "warning";
        uidata.showCalc = true;
      } else if (uidata.ampelZulage === 2) {
        color = "error";
        uidata.showCalc = true;
      } else if (uidata.ampelZulage === 0) {
        color = "warning";
        uidata.showCalc = true;
      }
    } else {
      if (uidata.ampelZulage === 1) {
        color = "success";
        uidata.showCalc = false;

      } else if (uidata.ampelZulage === 2) {
        color = "warning";
        uidata.showCalc = true;

      } else if (uidata.ampelZulage === 3) {
        color = "error";
        uidata.showCalc = false;
      }
    }
    if (color) {
      if (isStatus) {
        uidata.statusAmpel = color;
      } else {
        uidata.colorZulagen = color;
      }
    }
  }
}
